import React from 'react';
import { FiCheck, FiChevronDown, FiSearch, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { requestBrochure } from '../../services/brochureApi';
import { countryCodes } from '../../constants/countryCodes';

type IconComponent = React.ComponentType<{ className?: string }>;
const XIcon = FiX as IconComponent;
const ChevronDownIcon = FiChevronDown as IconComponent;
const SearchIcon = FiSearch as IconComponent;
const CheckIcon = FiCheck as IconComponent;

interface DownloadBrochureModalProps {
  isOpen: boolean;
  onClose: () => void;
  solutionSlug: string;
  solutionTitle: string;
  solutionDescription: string;
  brochurePdf: string;
}

const DownloadBrochureModal: React.FC<DownloadBrochureModalProps> = ({
  isOpen,
  onClose,
  solutionSlug,
  solutionTitle,
  solutionDescription,
  brochurePdf,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState('');
  const [isCodeDropdownOpen, setIsCodeDropdownOpen] = React.useState(false);
  const [countrySearch, setCountrySearch] = React.useState('');
  const [formData, setFormData] = React.useState({
    countryCode: '+91',
    phoneNumber: '',
    email: '',
  });
  const [fieldError, setFieldError] = React.useState('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setSubmitMessage('');
    setIsCodeDropdownOpen(false);
    setCountrySearch('');
  }, [isOpen]);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target as Node)) {
        setIsCodeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  if (!isOpen) return null;

  const translatedSolutionTitle = solutionSlug
    ? t(`ourSolutionSection.items.${solutionSlug}.title`, { defaultValue: solutionTitle })
    : solutionTitle;
  const translatedSolutionDescription = solutionSlug
    ? t(`ourSolutionSection.items.${solutionSlug}.description`, { defaultValue: solutionDescription })
    : solutionDescription;

  const selectedCountry = countryCodes.find((item) => item.code === formData.countryCode) || {
    name: 'India',
    code: '+91',
  };

  const filteredCountries = countryCodes.filter((item) => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) return true;
    return item.name.toLowerCase().includes(query) || item.code.toLowerCase().includes(query);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError('');
    setIsSubmitting(true);
    setSubmitMessage('');

    const cleanedPhone = formData.phoneNumber.replace(/[^\d]/g, '');
    const cleanedEmail = formData.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (cleanedPhone.length < 6 || cleanedPhone.length > 15) {
      setFieldError(t('downloadBrochureModal.phoneValidation', { defaultValue: 'Phone number must be 6 to 15 digits.' }));
      setIsSubmitting(false);
      return;
    }

    if (!emailRegex.test(cleanedEmail)) {
      setFieldError(t('downloadBrochureModal.emailValidation', { defaultValue: 'Please enter a valid email address.' }));
      setIsSubmitting(false);
      return;
    }

    try {
      await requestBrochure({
        solutionTitle: translatedSolutionTitle,
        solutionDescription: translatedSolutionDescription,
        countryCode: formData.countryCode,
        phoneNumber: cleanedPhone,
        email: cleanedEmail,
      });
      setSubmitMessage(t('downloadBrochureModal.success', { defaultValue: 'Request submitted successfully.' }));
      if (brochurePdf) {
        const downloadLink = document.createElement('a');
        downloadLink.href = brochurePdf;
        downloadLink.download = `${translatedSolutionTitle || 'brochure'}.pdf`;
        downloadLink.target = '_blank';
        downloadLink.rel = 'noopener noreferrer';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
      setFormData((prev) => ({ ...prev, phoneNumber: '', email: '' }));
    } catch (error) {
      setSubmitMessage(
        error instanceof Error
          ? error.message
          : t('downloadBrochureModal.failed', { defaultValue: 'Failed to submit request.' })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/45 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              {t('downloadBrochureModal.title', { defaultValue: 'Download Brochure' })}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              {t('downloadBrochureModal.subtitlePrefix', {
                defaultValue: 'Share your contact details to receive the brochure for',
              })}{' '}
              <span className="font-semibold text-slate-700">{translatedSolutionTitle}</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label={t('downloadBrochureModal.closeAria', { defaultValue: 'Close modal' })}
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div ref={dropdownRef} className="relative">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('downloadBrochureModal.countryCode', { defaultValue: 'Country Code' })}
            </label>
            <button
              type="button"
              onClick={() => setIsCodeDropdownOpen((prev) => !prev)}
              className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-left text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            >
              <span>{selectedCountry.name} ({selectedCountry.code})</span>
              <ChevronDownIcon className={`h-4 w-4 text-slate-500 transition-transform ${isCodeDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isCodeDropdownOpen && (
              <div className="absolute left-0 right-0 top-full z-30 mt-2 rounded-xl border border-slate-200 bg-white shadow-xl">
                <div className="border-b border-slate-100 p-2">
                  <div className="relative">
                    <SearchIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      placeholder={t('downloadBrochureModal.countrySearchPlaceholder', { defaultValue: 'Search country or code' })}
                      className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    />
                  </div>
                </div>
                <div
                  data-lenis-prevent
                  className="max-h-48 overflow-y-auto py-1"
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                >
                  {filteredCountries.map((item) => (
                    <button
                      key={`${item.name}-${item.code}`}
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, countryCode: item.code }));
                        setIsCodeDropdownOpen(false);
                      }}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-teal-50"
                    >
                      <span>{item.name} ({item.code})</span>
                      {formData.countryCode === item.code && <CheckIcon className="h-4 w-4 text-teal-600" />}
                    </button>
                  ))}
                  {filteredCountries.length === 0 && (
                    <p className="px-3 py-2 text-sm text-slate-500">
                      {t('downloadBrochureModal.noCountryFound', { defaultValue: 'No country found.' })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="phoneNumber" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('downloadBrochureModal.phoneNumber', { defaultValue: 'Phone Number' })}
            </label>
            <input
              id="phoneNumber"
              type="tel"
              required
              value={formData.phoneNumber}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/[^\d]/g, '').slice(0, 15);
                setFormData((prev) => ({ ...prev, phoneNumber: digitsOnly }));
              }}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              placeholder={t('downloadBrochureModal.phonePlaceholder', { defaultValue: 'Enter phone number' })}
              minLength={6}
              maxLength={15}
              inputMode="numeric"
              autoComplete="tel-national"
            />
          </div>

          <div>
            <label htmlFor="email" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('downloadBrochureModal.email', { defaultValue: 'Email' })}
            </label>
            <input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value.trimStart() }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              placeholder={t('downloadBrochureModal.emailPlaceholder', { defaultValue: 'Enter email address' })}
              maxLength={254}
              autoComplete="email"
            />
          </div>

          {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}
          {submitMessage && <p className="text-sm text-slate-600">{submitMessage}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              {t('downloadBrochureModal.cancel', { defaultValue: 'Cancel' })}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting
                ? t('downloadBrochureModal.submitting', { defaultValue: 'Submitting...' })
                : t('downloadBrochureModal.submit', { defaultValue: 'Submit' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DownloadBrochureModal;
